import { ref, computed } from 'vue';
import { supabase } from '@/services/supabase';
import type { TaskDocProfile, TaskInsert, TaskUpdate } from '@/features/task-management';

/**
 * Hook for managing tasks from the task-doc-profile view and tasks table
 * @returns Functions and state for working with tasks
 */
export function useTasks() {
  // Use TaskDocProfile type for type safety
  const tasks = ref<TaskDocProfile[]>([]);
  const selectedTask = ref<TaskDocProfile | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Computed property to get overdue tasks
   */
  const overdueTasks = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return tasks.value.filter(task => {
      if (!task.due_at || task.done_at) return false;
      const dueDate = new Date(task.due_at);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });
  });

  /**
   * Computed property to get tasks due today
   */
  const todayTasks = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return tasks.value.filter(task => {
      if (!task.due_at || task.done_at) return false;
      const dueDate = new Date(task.due_at);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate.getTime() === today.getTime();
    });
  });

  /**
   * Computed property to get completed tasks
   */
  const completedTasks = computed(() => {
    return tasks.value.filter(task => task.done_at !== null);
  });

  /**
   * Computed property to get tasks with action='UPLOAD'
   */
  const uploadActionTasks = computed(() => {
    return tasks.value.filter(task => task.action === 'UPLOAD');
  });

  /**
   * Computed property to get tasks with action='REVIEW' which are not completed
   */
  
  const openReviewTasks = computed(() => {
    return tasks.value.filter(task => task.action === 'REVIEW' && task.done_at === null);
  });

  /**
   * Fetch all tasks from the task_doc_profile view
   */
  async function fetchTasks() {
    loading.value = true;
    console.log('Fetching tasks from task_doc_profile view...');
    
    try {
      const { data, error: err } = await supabase
        .from('task_doc_profile')
        .select('*')
        .order('due_at', { ascending: true });
      
      if (err) {
        console.error('Error fetching tasks from task_doc_profile:', err);
        throw err;
      }
      
      console.log('Tasks fetched successfully:', data);
      tasks.value = data || [];
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch a single task by ID
   * @param id The task ID to fetch
   * @returns The task or null if not found
   */
  async function fetchTaskById(id: string) {
    loading.value = true;
    
    try {
      const { data, error: err } = await supabase
        .from('task_doc_profile')
        .select('*')
        .eq('id', id)
        .single();
      
      if (err) throw err;
      
      selectedTask.value = data;
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new task
   * @param task The task data to create
   * @returns The created task or null if there was an error
   */
  async function createTask(task: TaskInsert) {
    loading.value = true;
    
    try {
      const { data, error: err } = await supabase
        .from('tasks')
        .insert({
          ...task,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (err) throw err;
      
      // Refresh tasks after creating a new one
      await fetchTasks();
      
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      console.error('Error creating task:', error.value);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing task
   * @param id The ID of the task to update
   * @param updates The task data to update
   * @returns The updated task or null if there was an error
   */
  async function updateTask(id: string, updates: TaskUpdate) {
    loading.value = true;
    
    try {
      const { data, error: err } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (err) throw err;
      
      // Refresh tasks after updating
      await fetchTasks();
      
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      console.error('Error updating task:', error.value);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Mark a task as complete
   * @param id The ID of the task to mark as complete
   * @param description The description of the task to mark as complete
   * @returns The updated task or null if there was an error
   */
  async function completeTask(id: string, description: string) {
    return updateTask(id, {
      description: description,
      done_at: new Date().toISOString()
    });
  }

  /**
   * Mark a task as incomplete (reopen)
   * @param id The ID of the task to mark as incomplete
   * @returns The updated task or null if there was an error
   */
  async function reopenTask(id: string) {
    return updateTask(id, {
      done_at: null
    });
  }

  /**
   * Delete a task
   * @param id The ID of the task to delete
   * @returns True if successful, false otherwise
   */
  async function deleteTask(id: string) {
    loading.value = true;
    
    try {
      const { error: err } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);
      
      if (err) throw err;
      
      // Refresh tasks after deleting
      await fetchTasks();
      
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      console.error('Error deleting task:', error.value);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Subscribe to real-time updates for tasks
   * @param callback Function to call when tasks are updated
   * @returns The subscription that can be used to unsubscribe
   */
  function subscribeToTasks(callback: (payload: any) => void) {
    return supabase
      .channel('tasks-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        callback
      )
      .subscribe();
  }

  return {
    tasks,
    selectedTask,
    loading,
    error,
    overdueTasks,
    todayTasks,
    completedTasks,
    uploadActionTasks,
    openReviewTasks,
    fetchTasks,
    fetchTaskById,
    createTask,
    updateTask,
    completeTask,
    reopenTask,
    deleteTask,
    subscribeToTasks
  };
}